import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCampaign } from "../context/CampaignContext";
import Logo from "../components/Logo";

const progressSteps = [
  { id: "setup",   label: "Business Setup",   done: true },
  { id: "goal",    label: "Campaign Goal",    done: true },
  { id: "type",    label: "Campaign Type",    done: false, current: true },
  { id: "builder", label: "Campaign Builder", done: false },
  { id: "launch",  label: "Launch Guide",     done: false },
  { id: "results", label: "Track Results",    done: false },
];

const helpItems = [
  { icon: "🔍", title: "What is a Search Ad?",              desc: "Search ads appear when someone types a keyword into Google. You only pay when they click." },
  { icon: "▶️", title: "What is a YouTube Ad?",             desc: "YouTube ads appear before or during videos. Great for brand awareness and visual products." },
  { icon: "💰", title: "How much budget should I start with?", desc: "For beginners, $10–$20/day is the sweet spot. Enough data without overspending." },
  { icon: "⚠️", title: "Common beginner mistakes to avoid", desc: "Targeting too many locations, using wrong campaign goals, running ads without tracking results." },
];

const navItems = [
  { id: "home",      icon: "🏠", label: "Home",          path: "/dashboard" },
  { id: "build",     icon: "⚙️", label: "Build Campaign", path: "/setup" },
  { id: "guide",     icon: "📋", label: "Launch Guide",   path: "/launch-guide" },
  { id: "templates", icon: "📁", label: "Templates",      path: "/dashboard" },
  { id: "profile",   icon: "👤", label: "Profile",        path: "/dashboard" },
];

export default function Screen2() {
  const navigate                  = useNavigate();
  const { user, isPaid, userName } = useAuth();

  // Route guard — free users who land here directly get sent to upgrade
  useEffect(() => {
    if (user && !isPaid) {
      navigate('/upgrade');
    }
  }, [user, isPaid, navigate]);
  const { userSelections }        = useCampaign();
  const [activeNav, setActiveNav] = useState("home");
  const [openHelp, setOpenHelp]   = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);

  const doneCount = progressSteps.filter(s => s.done).length;
  const percent   = Math.round((doneCount / progressSteps.length) * 100);

  const handleBuild = () => isPaid ? navigate("/setup") : navigate("/upgrade");

  // Dynamic labels based on what the user selected in onboarding
  const businessLabels = {
    realestate: "real estate",
    local:      "local service",
    ecommerce:  "e-commerce",
    coach:      "coaching / consulting",
    creator:    "content creation",
    other:      "business",
  };
  const goalLabels = {
    leads:    "lead generation",
    calls:    "phone calls",
    traffic:  "website traffic",
    youtube:  "YouTube views",
    awareness:"brand awareness",
  };
  const businessLabel = businessLabels[userSelections?.businessType] || "business";
  const goalLabel     = goalLabels[userSelections?.goal]             || "lead generation";

  // Avatar initials from real name or email
  const initials = userName
    ? userName.slice(0,2).toUpperCase()
    : user?.email?.slice(0,2).toUpperCase() || "PL";

  return (
    <div style={s.page}>
      {/* Header */}
      <header style={s.header}>
        <Logo size={34} showText={true} textSize={17} />
        <div style={s.headerRight}>
          <button style={s.iconBtn} onClick={() => setNotifOpen(!notifOpen)}>
            🔔<span style={s.notifDot} />
          </button>
          <div style={s.avatar}>{initials}</div>
        </div>
      </header>

      {notifOpen && (
        <div style={s.notifDropdown}>
          <div style={s.notifItem}><span>💡</span><span style={s.notifTxt}>Your campaign type is the next step — it only takes 2 minutes.</span></div>
          <div style={s.notifItem}><span>🎉</span><span style={s.notifTxt}>Welcome to PilotLeadGen! Your profile is ready.</span></div>
        </div>
      )}

      <div style={s.body}>
        {/* Welcome */}
        <div style={s.welcomeCard}>
          <div style={s.welcomeLeft}>
            <div style={s.greeting}>👋 Welcome, {userName}</div>
            <h2 style={s.welcomeTitle}>Let's build your first campaign</h2>
            <p style={s.welcomeSub}>You're setting up your first Google Ads campaign for a <strong>real estate business</strong> with a <strong>lead generation</strong> goal.</p>
            <p style={s.welcomeNote}>We'll guide you step by step so you can launch with confidence.</p>
          </div>
          <div style={s.welcomeRight}>
            <div style={s.circleWrap}>
              <svg width="90" height="90" viewBox="0 0 90 90">
                <circle cx="45" cy="45" r="38" fill="none" stroke="#e2e8f0" strokeWidth="7" />
                <circle cx="45" cy="45" r="38" fill="none" stroke="#6366f1" strokeWidth="7"
                  strokeDasharray={`${2*Math.PI*38}`}
                  strokeDashoffset={`${2*Math.PI*38*(1-percent/100)}`}
                  strokeLinecap="round" transform="rotate(-90 45 45)" />
              </svg>
              <div style={s.circleLabel}>
                <div style={s.circlePct}>{percent}%</div>
                <div style={s.circleSub}>done</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recommended Next Step */}
        <div style={s.nextCard}>
          <div style={s.nextBadge}>Recommended Next Step</div>
          <h3 style={s.nextTitle}>Choose your campaign type</h3>
          <p style={s.nextDesc}>Based on your goal of getting <strong>leads</strong>, we recommend starting with a <strong>Search campaign</strong>.</p>
          <button style={s.nextBtn} onClick={handleBuild}>Continue →</button>
        </div>

        {/* Action Cards */}
        <div style={s.sectionLabel}>Quick Actions</div>
        <div style={s.actionGrid}>
          {[
            { icon:"🚀", title:"Start Building My Campaign",  desc:"Create your campaign structure, keywords, and ad copy.", btn:"Start", path:"/setup" },
            { icon:"⏩", title:"Continue Where I Stopped",    desc:"Pick up from your last completed step.",                btn:"Continue", path:"/setup" },
            { icon:"📋", title:"Open Launch Guide",           desc:"Follow simple click-by-click setup inside Google Ads.", btn:"Open", path:"/launch-guide" },
            { icon:"📁", title:"View Templates",              desc:"Browse ready-made headlines, descriptions, and keyword packs.", btn:"Browse", path:"/dashboard" },
          ].map((c, i) => (
            <div key={i} style={{ ...s.actionCard, gridColumn: i === 0 ? "1 / -1" : "auto" }}>
              <div style={s.actionIcon}>{c.icon}</div>
              <div style={s.actionTitle}>{c.title}</div>
              <div style={s.actionDesc}>{c.desc}</div>
              <button style={s.actionBtn} onClick={() => isPaid || c.path === "/dashboard" ? navigate(c.path) : navigate("/upgrade")}>
                {c.btn} →
              </button>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div style={s.sectionLabel}>Your Progress</div>
        <div style={s.progressCard}>
          <div style={s.progressBarWrap}>
            <div style={s.progressBarBg}><div style={{ ...s.progressBarFill, width:`${percent}%` }} /></div>
            <span style={s.progressBarLabel}>{percent}% complete</span>
          </div>
          <div style={s.stepList}>
            {progressSteps.map((step, i) => (
              <div key={step.id} style={s.stepRow}>
                <div style={s.stepRowLeft}>
                  <div style={{ ...s.stepIcon, background: step.done ? "#6366f1" : step.current ? "#fff" : "#f1f5f9", border: step.current ? "2px solid #6366f1" : "none" }}>
                    {step.done ? <span style={{ color:"#fff", fontSize:11 }}>✓</span> : <span style={{ color: step.current ? "#6366f1" : "#94a3b8", fontSize:11, fontWeight:800 }}>{i+1}</span>}
                  </div>
                  {i < progressSteps.length - 1 && <div style={{ ...s.stepConnector, background: step.done ? "#6366f1" : "#e2e8f0" }} />}
                </div>
                <div style={s.stepRowRight}>
                  <span style={{ ...s.stepLabel2, color: step.done ? "#1e293b" : step.current ? "#6366f1" : "#94a3b8", fontWeight: step.current ? 700 : step.done ? 600 : 400 }}>{step.label}</span>
                  {step.done && <span style={s.doneTag}>Completed</span>}
                  {step.current && <span style={s.currentTag}>Next</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Help */}
        <div style={s.sectionLabel}>Quick Help</div>
        <div style={s.helpList}>
          {helpItems.map((h, i) => (
            <div key={i} style={s.helpCard}>
              <button style={s.helpHeader} onClick={() => setOpenHelp(openHelp === i ? null : i)}>
                <div style={s.helpHeaderLeft}><span style={s.helpIcon}>{h.icon}</span><span style={s.helpTitle}>{h.title}</span></div>
                <span style={{ fontSize:14, color:"#94a3b8", transform: openHelp === i ? "rotate(180deg)" : "rotate(0deg)", display:"block", transition:"transform 0.2s" }}>▾</span>
              </button>
              {openHelp === i && <div style={s.helpBody}>{h.desc}</div>}
            </div>
          ))}
        </div>

        {!isPaid && (
          <div style={s.upgradeBanner}>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:"#4f46e5", marginBottom:4 }}>🔒 Unlock Full Campaign Builder</div>
              <div style={{ fontSize:13, color:"#6366f1", fontFamily:"sans-serif" }}>Upgrade to Starter to access keywords, ad copy, and the launch guide.</div>
            </div>
            <button style={{ padding:"10px 20px", borderRadius:10, background:"linear-gradient(135deg,#6366f1,#818cf8)", color:"#fff", border:"none", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"sans-serif", whiteSpace:"nowrap" }} onClick={() => navigate("/upgrade")}>
              Upgrade →
            </button>
          </div>
        )}

        <div style={{ height:80 }} />
      </div>

      {/* Bottom Nav */}
      <nav style={s.bottomNav}>
        {navItems.map(n => {
          const on = activeNav === n.id;
          return (
            <button key={n.id} style={{ ...s.navItem, color: on ? "#6366f1" : "#94a3b8" }} onClick={() => { setActiveNav(n.id); navigate(n.path); }}>
              <span style={{ fontSize:20 }}>{n.icon}</span>
              <span style={{ fontSize:10, fontFamily:"sans-serif", fontWeight: on ? 700 : 400, color: on ? "#6366f1" : "#94a3b8" }}>{n.label}</span>
              {on && <div style={s.navDot} />}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

const s = {
  page:           { minHeight:"100vh", background:"linear-gradient(160deg,#f8faff 0%,#eef2ff 60%,#f0fdf4 100%)", fontFamily:"'Georgia',serif", position:"relative" },
  header:         { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 24px", background:"#fff", borderBottom:"1px solid #e2e8f0", boxShadow:"0 1px 8px rgba(0,0,0,0.05)", position:"sticky", top:0, zIndex:200 },
  logo:           { display:"flex", alignItems:"center", gap:10 },
  mark:           { width:34, height:34, borderRadius:9, background:"linear-gradient(135deg,#6366f1,#818cf8)", color:"#fff", fontWeight:800, fontSize:16, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"sans-serif" },
  logoTxt:        { fontSize:17, fontWeight:800, color:"#1e293b", fontFamily:"sans-serif", letterSpacing:"-0.3px" },
  headerRight:    { display:"flex", alignItems:"center", gap:14 },
  iconBtn:        { background:"none", border:"none", fontSize:20, cursor:"pointer", position:"relative", padding:4 },
  notifDot:       { position:"absolute", top:2, right:2, width:8, height:8, borderRadius:"50%", background:"#ef4444", border:"2px solid #fff" },
  avatar:         { width:36, height:36, borderRadius:"50%", background:"linear-gradient(135deg,#f59e0b,#fbbf24)", color:"#fff", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"sans-serif" },
  notifDropdown:  { position:"fixed", top:64, right:24, zIndex:300, background:"#fff", borderRadius:14, boxShadow:"0 8px 32px rgba(0,0,0,0.12)", border:"1px solid #e2e8f0", width:300, padding:8 },
  notifItem:      { display:"flex", gap:10, padding:"12px 14px", borderRadius:10, alignItems:"flex-start" },
  notifTxt:       { fontSize:13, color:"#334155", fontFamily:"sans-serif", lineHeight:1.5 },
  body:           { padding:"20px 20px 0", maxWidth:640, margin:"0 auto" },
  welcomeCard:    { background:"#fff", borderRadius:20, border:"1px solid #e2e8f0", boxShadow:"0 4px 24px rgba(99,102,241,0.08)", padding:"24px 22px", marginBottom:16, display:"flex", alignItems:"center", gap:16 },
  welcomeLeft:    { flex:1 },
  greeting:       { fontSize:13, color:"#6366f1", fontWeight:700, fontFamily:"sans-serif", marginBottom:6 },
  welcomeTitle:   { fontSize:20, fontWeight:700, color:"#0f172a", margin:"0 0 8px", lineHeight:1.3 },
  welcomeSub:     { fontSize:14, color:"#475569", lineHeight:1.55, margin:"0 0 6px", fontFamily:"sans-serif" },
  welcomeNote:    { fontSize:13, color:"#64748b", fontFamily:"sans-serif", margin:0 },
  welcomeRight:   { flexShrink:0 },
  circleWrap:     { position:"relative", width:90, height:90 },
  circleLabel:    { position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center" },
  circlePct:      { fontSize:18, fontWeight:800, color:"#6366f1", fontFamily:"sans-serif", lineHeight:1 },
  circleSub:      { fontSize:10, color:"#94a3b8", fontFamily:"sans-serif" },
  nextCard:       { background:"linear-gradient(135deg,#6366f1,#818cf8)", borderRadius:18, padding:"22px 22px", marginBottom:20, boxShadow:"0 6px 28px rgba(99,102,241,0.3)" },
  nextBadge:      { display:"inline-block", background:"rgba(255,255,255,0.2)", color:"#fff", fontSize:11, fontWeight:700, padding:"3px 12px", borderRadius:20, fontFamily:"sans-serif", marginBottom:10, letterSpacing:"0.5px" },
  nextTitle:      { fontSize:20, fontWeight:700, color:"#fff", margin:"0 0 8px" },
  nextDesc:       { fontSize:14, color:"rgba(255,255,255,0.85)", lineHeight:1.55, margin:"0 0 18px", fontFamily:"sans-serif" },
  nextBtn:        { padding:"12px 24px", borderRadius:10, background:"#fff", color:"#6366f1", border:"none", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"sans-serif" },
  sectionLabel:   { fontSize:12, fontWeight:700, color:"#94a3b8", fontFamily:"sans-serif", letterSpacing:"1px", textTransform:"uppercase", marginBottom:12 },
  actionGrid:     { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:24 },
  actionCard:     { background:"#fff", borderRadius:16, border:"1.5px solid #e2e8f0", padding:"18px 16px", display:"flex", flexDirection:"column", gap:6 },
  actionIcon:     { fontSize:26, marginBottom:2 },
  actionTitle:    { fontSize:14, fontWeight:700, color:"#1e293b", fontFamily:"sans-serif" },
  actionDesc:     { fontSize:12, color:"#64748b", lineHeight:1.4, fontFamily:"sans-serif", flex:1 },
  actionBtn:      { marginTop:10, padding:"8px 0", background:"transparent", border:"1.5px solid #c7d2fe", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"sans-serif", color:"#6366f1" },
  progressCard:   { background:"#fff", borderRadius:18, border:"1px solid #f1f5f9", boxShadow:"0 2px 16px rgba(99,102,241,0.06)", padding:"22px 20px", marginBottom:24 },
  progressBarWrap:{ display:"flex", alignItems:"center", gap:12, marginBottom:20 },
  progressBarBg:  { flex:1, height:6, borderRadius:99, background:"#e2e8f0", overflow:"hidden" },
  progressBarFill:{ height:"100%", borderRadius:99, background:"linear-gradient(90deg,#6366f1,#818cf8)", transition:"width 0.4s ease" },
  progressBarLabel:{ fontSize:12, fontWeight:700, color:"#6366f1", fontFamily:"sans-serif", whiteSpace:"nowrap" },
  stepList:       { display:"flex", flexDirection:"column" },
  stepRow:        { display:"flex", gap:14, alignItems:"flex-start", minHeight:44 },
  stepRowLeft:    { display:"flex", flexDirection:"column", alignItems:"center", width:26, flexShrink:0 },
  stepIcon:       { width:26, height:26, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, zIndex:1 },
  stepConnector:  { width:2, flex:1, minHeight:16, marginTop:2 },
  stepRowRight:   { display:"flex", alignItems:"center", gap:8, paddingBottom:16, flex:1 },
  stepLabel2:     { fontSize:14, fontFamily:"sans-serif" },
  doneTag:        { fontSize:10, fontWeight:700, color:"#059669", background:"#dcfce7", padding:"2px 8px", borderRadius:20, fontFamily:"sans-serif" },
  currentTag:     { fontSize:10, fontWeight:700, color:"#6366f1", background:"#eef2ff", padding:"2px 8px", borderRadius:20, fontFamily:"sans-serif" },
  helpList:       { display:"flex", flexDirection:"column", gap:10, marginBottom:20 },
  helpCard:       { background:"#fff", borderRadius:14, border:"1px solid #f1f5f9", boxShadow:"0 1px 8px rgba(0,0,0,0.04)", overflow:"hidden" },
  helpHeader:     { width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", background:"none", border:"none", cursor:"pointer", textAlign:"left" },
  helpHeaderLeft: { display:"flex", alignItems:"center", gap:10 },
  helpIcon:       { fontSize:18, flexShrink:0 },
  helpTitle:      { fontSize:14, fontWeight:600, color:"#1e293b", fontFamily:"sans-serif" },
  helpBody:       { padding:"0 16px 16px 44px", fontSize:13, color:"#64748b", lineHeight:1.6, fontFamily:"sans-serif" },
  upgradeBanner:  { background:"#eef2ff", border:"1.5px solid #c7d2fe", borderRadius:14, padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, marginBottom:20, flexWrap:"wrap" },
  bottomNav:      { position:"fixed", bottom:0, left:0, right:0, background:"#fff", borderTop:"1px solid #e2e8f0", display:"flex", justifyContent:"space-around", padding:"8px 0 12px", boxShadow:"0 -2px 16px rgba(0,0,0,0.06)", zIndex:200 },
  navItem:        { display:"flex", flexDirection:"column", alignItems:"center", gap:3, background:"none", border:"none", cursor:"pointer", padding:"4px 12px", position:"relative" },
  navDot:         { position:"absolute", bottom:-4, left:"50%", transform:"translateX(-50%)", width:4, height:4, borderRadius:"50%", background:"#6366f1" },
};